import {
  SubscriptionFrequency,
  FrequencyUnit,
  GroupedItemFlavors,
  BasicCatalogImage,
  BasicCategory,
  Doctor,
  IItemAndFlavorOptionsWithProduct,
  PatientKind,
  ListSearch,
  ProductLineGuaranteedAnalysisItem,
  BasicManufacturer,
} from "common/types";
import { RebateStatus } from "common/types/rebates";
import {
  IPromoCode,
  Rebate as RebateType,
  Incentive as IncentiveType,
  IncentiveStatus,
  PromotionKind,
} from "common/types/promotions";
import { Optional } from "common/utils/types";
import { DeliveryMethodKind, ItemFulfillmentOption } from "common/types/fulfillments";
import { Availability } from "common/types/catalog";

import { IFact, IVideo, SearchResponse, ModalStatus } from "client/common/types";
import type { Prescription } from "client/prescriptions/types";
import { InStockNotification } from "client/inbox/types";

import type { Block } from "../blocks/types";

// From app/catalog/constants
export enum Frequency {
  OneTime = "ONE_TIME",
  Subscription = "SUBSCRIPTION",
}

// From app/search/constants
export enum ClientAutosuggestKind {
  ProductLine = "productline",
  Category = "category",
  Manufacturer = "manufacturer",
}

export interface IProductIngredient {
  name: string;
  strength: string;
  display: string;
}

/** Matches catalog DiscountedPriceBreak — a price_breaks tier with the rebate applied per unit */
export type DiscountedPriceBreak = {
  high_qty: number;
  low_qty: number;
  non_subscription_discounted_price: number;
  subscription_discounted_price: number;
};

/** Matches catalog ProductController._build_item_info() return value */
export interface IProductItem {
  id: number;
  name: string;
  label: Optional<string>;
  compound_description: Optional<string>;
  description: Optional<string>;
  non_subscription_non_discounted_price: number;
  non_subscription_discounted_price: number;
  subscription_discounted_price: number;
  is_map_price_adjusted: boolean;
  units_count: number | null;
  dose_label: string | null;
  subscription_incentive_discount_percentage: string;
  hide_subscription_discount_percentage: boolean;
  is_available: boolean;
  is_subscribable: boolean;
  images: BasicCatalogImage[];
  active_prescriptions: Prescription[];
  default_quantity: number;
  default_delivery_method: DeliveryMethodKind;
  default_flavor_selection: Optional<number>;
  incentives: IncentiveType[];
  rebates: RebateType[];
  has_promotions: boolean;
  recommended_frequency: SubscriptionFrequency;
  frequency_options: SubscriptionFrequency[];
  flavor_options: GroupedItemFlavors[];
  client_state_restriction_message: Optional<string>;
  fulfillment_options: ItemFulfillmentOption[];
  eligible_for_autoship_discounts: boolean;
  days_supply: number;
  duration_value: number;
  duration_unit: FrequencyUnit;
  is_backordered: boolean;
  in_stock_notification: Optional<InStockNotification>;
  is_vendor_fulfilled: boolean;
  is_klarna_eligible: boolean;
  discounted_price_breaks: DiscountedPriceBreak[];
  is_single_dispense: boolean;
}

export interface IRelatedProduct {
  id: number;
  label: string;
  description: string;
  is_available: boolean;
}

export interface IProductLine {
  id: number;
  products: IRelatedProduct[];
}

/** Matches catalog ProductLineController.get_client_product_info() return value */
export interface IProductLineProduct {
  id: number;
  label: Optional<string>;
  description: Optional<string>;
  items: IProductItem[];
  name: string;
  preview_image: string;
}

/**
 * Matches catalog ProductLineController.get_minimal_client_product_info() return value.
 * Includes display fields for ProductSelect and minimal item info.
 */
export interface IPDPProduct {
  id: number;
  name: string;
  label: Optional<string>;
  description: Optional<string>;
  preview_image: string;
  items: IPDPProductItem[];
}

/**
 * Item structure returned by ProductLineController for the PDP.
 * Only contains id for identification and prescriptions for top-level display.
 */
export interface IPDPProductItem {
  id: number;
  active_prescriptions: Prescription[];
}

/**
 * Matches catalog ProductController.get_response() return value.
 * Used by useProductQuery() to fetch full product details.
 */
export interface IProductQueryResponse {
  id: number;
  name: string;
  label: Optional<string>;
  description: Optional<string>;
  preview_image: string;
  items: IProductItem[];
}

/** Matches catalog ProductLineSupportItemSerializer */
export interface IProductLineSupportItem {
  id: number;
  title: string;
  body: string;
}

/** Matches search ProductLineController.get_clinic_review_summary */
// TODO sc-22058 update with client review summary fields too
export interface IProductLineReviewSummary {
  average_clinic_rating: number;
  num_clinic_reviews: number;
  average_client_rating: number;
  num_client_reviews: number;
}

/** Matches catalog ProductLineController.get_contents */
export interface IProductLineContent {
  id: number;
  block: Block;
}

/** Matches search ProductLineController.get_client_details_response */
export interface IProductLineDetails {
  id: number;
  name: string;
  brand_name: string;
  image: Optional<BasicCatalogImage>;
  banner_image: string;
  facts: IFact[];
  videos: IVideo[];
  primary_category_name: string;
  products: IPDPProduct[];
  short_description: string;
  description: string;
  has_multiple_manufacturers: boolean;
  ingredients: IProductIngredient[];
  safety_information: string;
  dosage_guide: string;
  safety_overview: string;
  prescription_required: boolean;
  support_items: IProductLineSupportItem[];
  usage_details: string;
  review_summary: IProductLineReviewSummary;
  doctor: Optional<Doctor>;
  msds_url: Optional<string>;
  contents: IProductLineContent[];
  prescribing_information_url: Optional<string>;
  feeding_guide: string;
  product_select_label: string;
  item_select_label: string;
  featured_categories: IFeaturedCategory[];
  /**
   * V2 of the carousel refactor: true when a FeaturedProductLine row exists
   * for this clinic+PL, independent of FeaturedCategory membership. Used to
   * render the "Your Vet's Choice" chip under the CarouselV2 feature.
   */
  is_featured_v2: boolean;
  guaranteed_analysis_items: ProductLineGuaranteedAnalysisItem[];
  guaranteed_analysis_fine_print: Optional<string>;
  should_show_employee_pricing_note: boolean;
  is_compound: boolean;
  /** Inline dict from ProductLineController.get_client_details_response — not BasicCategorySerializer */
  categories: { id: number; name: string }[];
}

/** Matches search ClientProductLineProductSerializer */
export interface IProductLineResultProduct {
  id: number;
  name: string;
  primary_label: Optional<string>;
  secondary_label: Optional<string>;
  main_image: Optional<BasicCatalogImage>;
}

/** Matches search _ProductLineResultFactSerializer */
export interface IProductLineResultFact {
  label: string;
  value: string;
}

/** Matches search ClientProductLineSerializer */
export interface ProductLineResult {
  id: number;
  name: string;
  brand_name: string;
  prescription_required: boolean;
  products: IProductLineResultProduct[];
  manufacturer: BasicManufacturer;
  categories: BasicCategory[];
  primary_category_name: string;
  patient_prescription_label: Optional<string>;
  promotion_label: string;
  description: string;
  image: Optional<BasicCatalogImage>;
  facts: IProductLineResultFact[];
  is_featured: boolean;
  is_loyalty_program_featured: boolean;
}

/** The product's item-range axis, e.g. { count: 7, label: "Counts" }. */
export type ItemDimension = {
  count: number;
  label: string;
};

/** Matches search ClientProductLineSearchResultSerializer */
export type ProductSearchResultProductLine = {
  id: number;
  name: string;
  brand_name: string;
  prescription_required: boolean;
  is_compound: boolean;
  manufacturer: BasicManufacturer;
  categories: BasicCategory[];
  primary_category_name: string;
  is_featured: boolean;
  is_loyalty_program_featured: boolean;
  client_description: string;
};

/** Mirrors the backend `search/constants.ProductCardBadgePrice`. */
export enum ProductCardBadgePrice {
  OneTime = "ONE_TIME",
  // Applies only when the shopper subscribes. Deals page only.
  Subscription = "SUBSCRIPTION",
}

/** Mirrors the backend `search/types.ProductCardBadge`. */
export type ProductCardBadge = {
  // Which promotion table `promotion_id` points at.
  source: PromotionKind;
  promotion_id: number;
  // Which price the saving comes off.
  applies_to: ProductCardBadgePrice;
  // The savings in cents, shown as "$X off" whenever there is no rate to state.
  savings_amount: number;
  // The promotion's configured rate (0.2 -> "20% off"); null means state dollars.
  percent: Optional<number>;
  // The promotion's configured ceiling in cents, set only alongside `percent`.
  max_savings: Optional<number>;
};

/** The product's featured (default) item and its prices. All monetary values are in cents. */
export type ProductSearchDefaultItem = {
  // The item, for linking the card straight to its product-detail variant.
  id: number;
  // Pack size for an optional per-unit display (e.g. 30, "tablet"); null when not sold by count.
  units_count: Optional<number>;
  unit_label: Optional<string>;
  // The one-time total the shopper pays.
  standard_price: number;
  // The pre-discount list total (always present); struck through on the card when it's higher.
  standard_list_price: number;
  // The subscription total the shopper would actually pay; null when there's no subscription discount.
  subscription_price: Optional<number>;
  // Distinct flavors the item comes in; null when 1 or fewer.
  flavor_count: Optional<number>;
  // The item's deals as chips, in render order; empty when none. Which kinds appear is decided
  // server-side per surface, so the card renders whatever it's handed.
  badges: ProductCardBadge[];
};

/** Matches search ClientProductSearchResultSerializer. */
export type ProductSearchResult = {
  id: number;
  name: string;
  full_name: string;
  primary_label: Optional<string>;
  secondary_label: Optional<string>;
  product_line: ProductSearchResultProductLine;
  patient_prescription_label: Optional<string>;
  promotion_label: string;
  main_image: Optional<BasicCatalogImage>;
  // null when the product has <=1 option.
  item_dimensions: Optional<ItemDimension>;
  // The card's selected/ranked fact chips; empty when none.
  card_facts: IProductLineResultFact[];
  // The featured item + its prices.
  default_item: ProductSearchDefaultItem;
};

/** Matches search ClientProductSearchView */
export type ProductSearch = SearchResponse<ProductSearchResult> & {
  filters: ProductSearchFilters;
  /** Whether `filters` holds every option for each field, with counts that ignore that field's own selection. */
  has_complete_filter_options: boolean;
};

/** Matches search ClientDealsProductSearchView */
export type ProductDeals = ProductSearch;

/** What a filter dimension's values can be: ids, species names, or a boolean. */
export type FilterValue = string | number | boolean;

/** Matches search SearchFilterOption */
export type SearchFilterOption<T extends FilterValue> = {
  value: T;
  label: string;
  count: number;
  /** Set on categories only, from the search payload; every other dimension is flat. */
  parent_id?: number | null;
};

export type ProductSearchFilters = {
  categories: SearchFilterOption<number>[];
  species: SearchFilterOption<PatientKind>[];
  manufacturers: SearchFilterOption<number>[];
  prescription_required: SearchFilterOption<boolean>[];
};

export type ProductLineSearchFilters = {
  categories: SearchFilterOption<number>[];
  species: SearchFilterOption<PatientKind>[];
  manufacturers: SearchFilterOption<number>[];
  prescription_required: SearchFilterOption<boolean>[];
};

/**
 * One filter dimension. `TSearch` is the surface's selection shape, `TFilterOptions` the shape
 * of the `filters` payload its response carries, and `TValue` this dimension's value type.
 */
export type FilterDefinition<
  TSearch,
  TValue extends FilterValue = FilterValue,
  TFilterOptions = ProductSearchFilters,
> = {
  key: string;
  label: string;
  singularLabel: string;
  optionsFrom(options: TFilterOptions): SearchFilterOption<TValue>[];
  selectedFrom(filters: TSearch): TValue[];
  toUpdate(next: TValue[]): Partial<TSearch>;
};

/**
 * Writes one definition at its own value type and widens it, so a single array can hold
 * dimensions keyed to ids, species names and booleans while each definition body stays
 * checked against its own `TValue`. The widening needs no cast: the three members are
 * methods, so their parameters are bivariant.
 */
export const defineFilter = <
  TSearch,
  TValue extends FilterValue,
  TFilterOptions = ProductSearchFilters,
>(
  definition: FilterDefinition<TSearch, TValue, TFilterOptions>,
): FilterDefinition<TSearch, FilterValue, TFilterOptions> => definition;

/** Matches search ClientProductLineSearchView */
export type ProductLineSearch = SearchResponse<ProductLineResult> & {
  filters: ProductLineSearchFilters;
  /** Whether `filters` holds every option for each field, with counts that ignore that field's own selection. */
  has_complete_filter_options: boolean;
};

/**
 * When we start a new search (ie when the q param changes)
 * we want to use those same filters for subsequent filtering on the same q param.
 *
 * This is because as we filter down the results we may loose filters because
 * the result set will continually get smaller, but we want to give the user
 * the ability to filter back up to the original result set.
 *
 * This does imply that visiting the same URL may give you
 * different (or no) filters depending on the filters that are already applied.
 *
 * TODO sc-32581 Remove once we have a stable BE filter set returned on each search
 * or a separate endpoint for getting the filters
 * https://app.shortcut.com/vetcove/story/32581/
 * */
export type FilterOptionsState<T extends Record<string, SearchFilterOption<any>[]>> = {
  q: string;
  filters: T;
};

export interface ProductLineListSearch extends ListSearch {
  category_ids?: number[];
  manufacturer_ids?: number[];
  species?: PatientKind[];
  /**
   * If important (prescribed, featured, etc.) product lines for this user should be displayed first
   * useful for browsing views where the user does not have free-text search
   */
  important_first?: boolean;
}

/**
 * Filters available for product searches.
 *
 * Only one of rebate_ids or incentive_ids can be used at a time
 */
export interface ProductSearchSearch extends ListSearch {
  category_ids?: number[];
  manufacturer_ids?: number[];
  species?: PatientKind[];
  incentive_ids?: number[];
  rebate_ids?: number[];
  prescription_required?: boolean[];
  product_ids?: number[];
  product_line_ids?: number[];
  /** A StorefrontProductCollection slug. The backend limits results to its products. */
  collection?: string;
}

/** Matches search ClientDealsProductSearchView */
export type ProductDealsSearch = Omit<ProductSearchSearch, "q">;

export interface SelectedProductFilters {
  category_ids: number[];
  manufacturer_ids: number[];
  species: PatientKind[];
  incentive_ids?: number[];
  rebate_ids?: number[];
  prescription_required: boolean[];
}

export interface SelectedPromotionProductFilters {
  category_ids: number[];
  manufacturer_ids: number[];
  species: PatientKind[];
  prescription_required: boolean[];
}

export interface SelectedCategoryProductLineFilters {
  manufacturer_ids: number[];
  species: PatientKind[];
  prescription_required: boolean[];
}

export interface IncentiveItemsSearch {
  page: number;
  page_size: number;
  category_ids?: number[];
  manufacturer_ids?: number[];
  species?: PatientKind[];
  prescription_required?: boolean[];
}

export type PromotionItems = SearchResponse<PromotionItem> & {
  filters: ProductSearchFilters;
  /** Whether `filters` holds every option for each field, with counts that ignore that field's own selection. */
  has_complete_filter_options: boolean;
};

export interface RebateItemsSearch {
  page: number;
  page_size: number;
  category_ids?: number[];
  manufacturer_ids?: number[];
  species?: PatientKind[];
  prescription_required?: boolean[];
}

export enum BrowseAllPatientKind {
  ALL = "ALL",
}

export type IBrowseFilterSpeciesKind = PatientKind | BrowseAllPatientKind;

export interface BrowseSearch {
  species: IBrowseFilterSpeciesKind;
}

export interface ClientAutosuggestResult {
  search_params: Partial<ProductLineListSearch>;
  display: string;
  kind: ClientAutosuggestKind;
}

export type ClientAutosuggest = ClientAutosuggestResult[];

export enum ProductLineTabKey {
  Overview = "overview",
  Usage = "usage",
  Ingredients = "ingredients",
  Safety = "safety",
  Reviews = "reviews",
  Faq = "faq",
}

export type ProductDetailSearch = {
  tab: ProductLineTabKey;
  modal: ModalStatus;
  product: number;
  item: number;
};

/** Matches ClientItemSerializer */
export interface IItemWithDetails extends IItemAndFlavorOptionsWithProduct {
  description: string;
  facts: IFact[];
  is_subscribable: boolean;
  is_vendor_fulfilled: boolean;
  default_flavor_selection: Optional<number>;
  in_stock_notification: Optional<InStockNotification>;
}

export interface IMedia {
  id: number;
  url: string;
  thumbnail_url: string;
}

/** Matches search ClientBrowseProductLineSerializer */
export interface BrowseProductLine {
  id: number;
  name: string;
  brand_name: string;
  prescription_required: boolean;
  categories: BasicCategory[];
  primary_category_name: string;
  description: string;
  image: Optional<BasicCatalogImage>;
  facts: IProductLineResultFact[];
  is_featured: boolean;
  is_loyalty_program_featured: boolean;
  patient_prescription_label: Optional<string>;
  promotion_label: string;
}

/** matches search ClientCategorySerializer */
export interface ClientCategory {
  id: number;
  slug: string;
  extended_name: string;
  description: string;
}

/** Matches search ClientBrowseCategorySerializerV2 */
export interface IClientBrowseCategory {
  id: number;
  slug: string;
  name: string;
  description: string;
  extended_name: string;
  product_lines: BrowseProductLine[];
}

/** Matches search ClientBrowseController.get_home_browse_data */
export interface IClientBrowse {
  categories: IClientBrowseCategory[];
  featured_product_lines: BrowseProductLine[];
}

/** Matches search _CarouselNestedProductLineSerializer */
export type CarouselDisplayNestedProductLine = {
  id: number;
  name: string;
  brand_name: string;
  prescription_required: boolean;
  categories: BasicCategory[];
  primary_category_name: string;
  description: string;
  image: Optional<BasicCatalogImage>;
  facts: IProductLineResultFact[];
  patient_prescription_label: Optional<string>;
};

/** Matches search CarouselProductLineSerializer */
export type CarouselDisplayProductLine = {
  id: number;
  product_line: CarouselDisplayNestedProductLine;
  is_featured: boolean;
  is_loyalty: boolean;
};

/** Matches search CarouselCategorySerializer */
export type CarouselDisplayCategory = {
  id: number;
  slug: string;
  name: string;
  extended_name: string;
  description: string;
};

/**
 * Matches search CarouselSerializer.
 *
 * `category === null` is the canonical "Top Sellers" carousel — see
 * `CarouselDisplayController` and `CarouselArrangement` on the BE. Renderers
 * branch on `carousel.category === null` and substitute `CategoryCard.TopSellers`
 * + a hard-coded title.
 */
export type CarouselDisplayCarousel = {
  id: number;
  category: CarouselDisplayCategory | null;
  product_lines: CarouselDisplayProductLine[];
};

/** Matches search CarouselDisplayController.get_carousel_display_data */
export type CarouselDisplay = {
  carousels: CarouselDisplayCarousel[];
};

/** A CarouselDisplayCarousel narrowed to the variant with a non-null category. */
export type CategoryCarousel = CarouselDisplayCarousel & {
  category: CarouselDisplayCategory;
};

/** Matches catalog ProductLineFeaturedCategoriesSerializer */
export interface IFeaturedCategory {
  id: number;
  name: string;
  extended_name: string;
}

/** matches promotions ClientPromotionProductLineSerializer */
type PromotionProductLine = {
  id: number;
  is_featured: boolean;
  name: string;
  description: string;
  product_select_label: string;
};

/** matches promotions ClientPromotionProductSerializer */
type PromotionProduct = {
  id: number;
  product_line: PromotionProductLine;
  name: string;
};

/** matches promotions ClientPromotionItemSerializer */
export type PromotionItem = {
  id: number;
  main_image: BasicCatalogImage;
  product: PromotionProduct;
  name: string;
  patient_prescription_label: Optional<string>;
  product_line_relative_name: string;
};

/** matches promotions ClientIncentiveDetailSerializer */
export type IncentiveDetails = {
  id: number;
  promo_title: string;
  promo_message: string;
  promo_details: string;
  start_date: Optional<string>;
  end_date: Optional<string>;
  promo_codes: IPromoCode[];
  status: IncentiveStatus;
};

/** matches promotions ClientRebateDetailSerializer */
export type RebateDetails = {
  id: number;
  promo_title: string;
  promo_message: string;
  banner_image_url: Optional<string>;
  prefooter_image_url: Optional<string>;
  start_date: Optional<string>;
  end_date: Optional<string>;
  promo_codes: IPromoCode[];
  promo_details: Optional<string>;
  status: RebateStatus;
};

type SubscriptionSelectItemPropsWithPricing = {
  is_subscribable: boolean;
  eligible_for_autoship_discounts: boolean;
  recommended_frequency: SubscriptionFrequency;
  subscription_incentive_discount_percentage: string;
  hide_subscription_discount_percentage?: boolean;
  frequency_options: SubscriptionFrequency[];
  subscription_discounted_price: number;
  non_subscription_discounted_price: number;
  non_subscription_non_discounted_price: number;
  discounted_price_breaks: DiscountedPriceBreak[];
};

type SubscriptionSelectItemPropsWithoutPricing = {
  is_subscribable: boolean;
  eligible_for_autoship_discounts: boolean;
  recommended_frequency: SubscriptionFrequency;
  subscription_incentive_discount_percentage: string;
  hide_subscription_discount_percentage?: boolean;
  frequency_options: SubscriptionFrequency[];
  subscription_discounted_price?: never;
  non_subscription_discounted_price?: never;
  non_subscription_non_discounted_price?: never;
  discounted_price_breaks?: never;
};

export type SubscriptionSelectItemProps =
  | SubscriptionSelectItemPropsWithoutPricing
  | SubscriptionSelectItemPropsWithPricing;

export type ProductLineAvailabilitySearch = {
  product?: number;
  item?: number;
};

export type ProductLineAvailabilityResponse = {
  full_name: string;
  availability: Availability;
};

/**
 * Search parameters for item add-to-cart pricing API
 * Matches ItemPricingController.resolve_item_pricing_in_context parameters
 */
export type ItemPricingSearch = {
  quantity: number;
  is_subscription_price: boolean;
};

/**
 * Item pricing response for add-to-cart pricing
 * Returns only the total price (with discounts, fees, and shipping included)
 * Backend: app/catalog/api/views.py:ClientAddToCartPricingView.get()
 */
export type ItemPricingResponse = {
  discounted_subtotal: number;
};
